import React, { useState, useEffect, useRef } from 'react';
import { FaPlay } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import CardItem from '../CardItem/CardItem';
import TemplateModal from '../TemplateModal/TemplateModal';
import SerieDetailsModal from '../SerieDetailsModal/SerieDetailsModal';
import './CollectionCarousel.css';

const CollectionCarousel = ({ title, contents }) => {
    const [index, setIndex] = useState(0);
    const [touchStart, setTouchStart] = useState(null);
    const [touchEnd, setTouchEnd] = useState(null);
    const [showTemplateModal, setShowTemplateModal] = useState(false);
    const [showSerieModal, setShowSerieModal] = useState(false);
    const [selectedContent, setSelectedContent] = useState(null);
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const carouselRef = useRef(null);
    const navigate = useNavigate();
    const minSwipeDistance = 50;

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 768);
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const handlePrev = () => {
        setIndex((prev) => (prev === 0 ? contents.length - 1 : prev - 1));
    };

    const handleNext = () => {
        setIndex((prev) => (prev === contents.length - 1 ? 0 : prev + 1));
    };

    const onTouchStart = (e) => {
        setTouchEnd(null);
        setTouchStart(e.targetTouches[0].clientX);
    };

    const onTouchMove = (e) => {
        setTouchEnd(e.targetTouches[0].clientX);
    };

    const onTouchEnd = () => {
        if (!touchStart || !touchEnd) return;

        const distance = touchStart - touchEnd;
        const isLeftSwipe = distance > minSwipeDistance;
        const isRightSwipe = distance < -minSwipeDistance;

        if (isLeftSwipe) {
            handleNext();
        } else if (isRightSwipe) {
            handlePrev();
        }
    };

    const handleClick = (content) => {
        if (content.status !== 'blocked') {
            if (content.type === "serie") {
                if (isMobile) {
                    navigate(`/serie_details/${content.id}`);
                } else {
                    setSelectedContent(content);
                    setShowSerieModal(true);
                }
            } else {
                const availableContents = contents.filter(content => content.status === 'available' && content.type !== 'serie');
                navigate(`/video/${content.id}`, { state: { playlist: availableContents } });
            }
        } else {
            setSelectedContent(content);
            setShowTemplateModal(true);
        }
    };

    const handleTemplateModalClose = () => {
        setShowTemplateModal(false);
        setSelectedContent(null);
    };

    const handleSerieModalClose = () => {
        setShowSerieModal(false);
        setSelectedContent(null);
    };

    // Calcula a posição do wrapper para centralizar o item ativo
    const getWrapperStyle = () => {
        const itemWidth = isMobile ? 77 : 52; // 75% + 2% margem no mobile, 50% + 2% margem no desktop
        const offset = itemWidth * index;
        const centerOffset = (100 - itemWidth) / 2; // Calcula o offset necessário para centralizar
        return {
            transform: `translateX(calc(${centerOffset}% - ${offset}%))`,
        };
    };

    return (
        <div className="collection-carousel">
            {title && <h2 className="collection-carousel-section-title">{title}</h2>}
            <div className="collection-carousel-container">
                <div
                    ref={carouselRef}
                    className="collection-carousel-wrapper"
                    style={getWrapperStyle()}
                    onTouchStart={onTouchStart}
                    onTouchMove={onTouchMove}
                    onTouchEnd={onTouchEnd}
                >
                    {contents.map((content, idx) => (
                        <div
                            key={content.contentId}
                            className={`collection-carousel-item ${idx === index ? 'active' : ''}`}
                            onClick={() => handleClick(content)}
                            style={{
                                cursor: content.status !== 'blocked' ? 'pointer' : 'not-allowed',
                                opacity: content.status !== 'blocked' ? (idx === index ? 1 : 0.5) : 0.3,
                            }}
                        >
                            <div className="collection-carousel-image-container">
                                <CardItem
                                    status={content.status}
                                    imageUrl={content.thumbnailUrl}
                                    onClick={() => handleClick(content)}
                                />
                                <div className="collection-carousel-play-icon">
                                    <FaPlay className="collection-carousel-item-icon" />
                                </div>
                            </div>
                            <div className="collection-carousel-content">
                                <h3 className="collection-carousel-title">{content.title}</h3>
                                <p className="collection-carousel-subtitle">{content.description}</p>
                            </div>
                        </div>
                    ))}
                </div>
                <button className="collection-carousel-button left" onClick={handlePrev}>‹</button>
                <button className="collection-carousel-button right" onClick={handleNext}>›</button>
                <div className="collection-carousel-indicators">
                    {contents.map((_, i) => (
                        <span
                            key={i}
                            className={`collection-carousel-dot ${i === index ? "active" : ""}`}
                            onClick={() => setIndex(i)}
                        />
                    ))}
                </div>
            </div>

            {showTemplateModal && selectedContent && (
                <TemplateModal
                    templateId={selectedContent.purchaseTemplateId}
                    onClose={handleTemplateModalClose}
                />
            )}

            {showSerieModal && selectedContent && (
                <SerieDetailsModal
                    serieId={selectedContent.id}
                    onClose={handleSerieModalClose}
                />
            )}
        </div>
    );
};

export default CollectionCarousel; 